import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { LockResetOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import unsplash from '../api/unsplash';
import GlobalSnackbar from './Snackbar';
import PasswordEditor, { getPasswordRequirements } from './PasswordEditor';
import PasswordPageShell from './PasswordPageShell';

const UpdatePasswordGeneric = ({
    verifyTokenRoute,
    changePasswordRoute,
    verifyOtpRoute,
    loginRoute,
}) => {
    const navigate = useNavigate();
    const [tokenStatus, setTokenStatus] = useState('loading');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [verifyOtpCodeModal, setVerifyOtpCodeModal] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [pendingUser, setPendingUser] = useState(null);
    const [openSnack, setOpenSnack] = useState(false);
    const [snack, setSnack] = useState({
        severity: 'error',
        messaggio: '',
    });
    const token = useMemo(
        () => new URLSearchParams(window.location.search).get('token'),
        []
    );

    const showMessage = (messaggio, severity = 'error') => {
        setSnack({ severity, messaggio });
        setOpenSnack(true);
    };

    useEffect(() => {
        const checkToken = async () => {
            if (!token) {
                setTokenStatus('invalid');
                return;
            }
            try {
                const response = await unsplash.post(verifyTokenRoute, {
                    token,
                });
                setTokenStatus(response.data.valid ? 'valid' : 'invalid');
            } catch {
                setTokenStatus('invalid');
            }
        };
        checkToken();
    }, [token, verifyTokenRoute]);

    const savePassword = async (event) => {
        event.preventDefault();
        const passwordIsValid = getPasswordRequirements(password).every(
            ({ valid }) => valid
        );
        if (!passwordIsValid) {
            showMessage('La password non rispetta tutti i requisiti');
            return;
        }
        if (password !== confirmPassword) {
            showMessage('Le password inserite non coincidono');
            return;
        }

        setSubmitting(true);
        try {
            const response = await unsplash.post(changePasswordRoute, {
                token,
                password,
                confirmPassword,
            });
            if (response.data.otpRequired) {
                setPendingUser({
                    token,
                    password,
                    confirmPassword,
                    userID: response.data.userID,
                });
                setVerifyOtpCodeModal(true);
                return;
            }
            if (response.data.status) {
                navigate(loginRoute);
                return;
            }
            showMessage(response.data.errors?.[0] || 'Errore imprevisto');
        } catch (error) {
            showMessage(
                error.response?.data?.errors?.[0] ||
                    'Errore durante il salvataggio'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmCode = async () => {
        try {
            const response = await unsplash.post(verifyOtpRoute, {
                ...pendingUser,
                verificationCode,
            });
            if (response.data.status) {
                navigate(loginRoute);
                return;
            }
            showMessage(response.data.errors?.[0] || 'Codice non valido');
        } catch {
            showMessage('Codice non valido');
        }
    };

    return (
        <PasswordPageShell
            icon={<LockResetOutlined />}
            title="Scegli una nuova password"
            subtitle="Proteggi il tuo account con una password robusta"
        >
            <GlobalSnackbar
                severity={snack.severity}
                messaggio={snack.messaggio}
                openSnack={openSnack}
                handleCloseSnack={(event, reason) => {
                    if (reason !== 'clickaway') setOpenSnack(false);
                }}
            />
            {tokenStatus === 'loading' ? (
                <Alert severity="info">Verifica del link in corso...</Alert>
            ) : tokenStatus === 'invalid' ? (
                <>
                    <Alert severity="warning">
                        Questo link non è valido, è scaduto oppure è già stato
                        utilizzato. La password precedente è ancora valida.
                    </Alert>
                    <Button
                        fullWidth
                        onClick={() => navigate(loginRoute)}
                        sx={{ mt: 2 }}
                    >
                        Torna alla login
                    </Button>
                </>
            ) : (
                <PasswordEditor
                    password={password}
                    setPassword={setPassword}
                    confirmPassword={confirmPassword}
                    setConfirmPassword={setConfirmPassword}
                    onSubmit={savePassword}
                    submitting={submitting}
                    submitLabel="Conferma nuova password"
                />
            )}

            <Dialog
                open={verifyOtpCodeModal}
                onClose={(event, reason) => {
                    if (
                        reason !== 'backdropClick' &&
                        reason !== 'escapeKeyDown'
                    ) {
                        setVerifyOtpCodeModal(false);
                    }
                }}
            >
                <DialogTitle>Verifica SMS</DialogTitle>
                <DialogContent>
                    Abbiamo inviato un codice al numero associato al tuo
                    account.
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Codice di verifica"
                        fullWidth
                        value={verificationCode}
                        onChange={(event) =>
                            setVerificationCode(event.target.value)
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setVerificationCode('');
                            setVerifyOtpCodeModal(false);
                        }}
                    >
                        Annulla
                    </Button>
                    <Button onClick={handleConfirmCode}>Conferma</Button>
                </DialogActions>
            </Dialog>
        </PasswordPageShell>
    );
};

export default UpdatePasswordGeneric;
